"use client";

import React, { useState, useRef } from "react";
import { SectionContentProps } from "../../types";
import EditableText from "../../core/EditableText";
import { PiUpload, PiX } from "react-icons/pi";

interface StatItem {
    icon?: string;
    image?: string;
    value: string;
    label: string;
}

export default function EducaeetStats({ content, onContentChange, isSelected }: SectionContentProps) {
    const primaryBg = content.colorPalette?.primary || "#074d3d"; // Deep forest emerald
    const fontFamily = content.fontFamily || "'Inter', 'Plus Jakarta Sans', sans-serif";
    const [uploadingIndex, setUploadingIndex] = useState<number | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const stats: StatItem[] = content.stats || [
        { icon: "course", value: "1090+", label: "Our Online Courses", image: "" },
        { icon: "instructor", value: "120+", label: "Our Instructors", image: "" },
        { icon: "video", value: "120+", label: "Total Video Lessons", image: "" },
        { icon: "student", value: "6000+", label: "Total Students Enrolled", image: "" }
    ];

    const iconMap: Record<string, JSX.Element> = {
        course: (
            <svg className="w-10 h-10 text-white stroke-[1.8]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <rect x="3" y="12" width="18" height="8" rx="1.5" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M2 20h20" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4l6 3.5-6 3.5-6-3.5L12 4z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 8.5v4" />
            </svg>
        ),
        instructor: (
            <svg className="w-10 h-10 text-white stroke-[1.8]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <circle cx="7" cy="7" r="2.5" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 19v-2a3 3 0 013-3h1" />
                <rect x="11" y="4" width="10" height="9" rx="1" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M11 17l5-4" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 20h6" />
            </svg>
        ),
        video: (
            <svg className="w-10 h-10 text-white stroke-[1.8]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <rect x="2" y="3" width="20" height="14" rx="2" />
                <circle cx="8" cy="10" r="2" />
                <polygon points="13,7 18,10 13,13" fill="currentColor" stroke="none" />
                <line x1="2" y1="20" x2="22" y2="20" strokeLinecap="round" />
                <circle cx="5" cy="20" r="1.5" fill="currentColor" stroke="none" />
            </svg>
        ),
        student: (
            <svg className="w-10 h-10 text-white stroke-[1.8]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <circle cx="12" cy="6" r="2.5" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M7 13a5 5 0 0110 0" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 19c2-1 4.5-1.5 8-0.5 3.5-1 6-0.5 8 0.5" />
                <line x1="12" y1="13.5" x2="12" y2="19" strokeLinecap="round" />
            </svg>
        )
    };

    const handleImageUpload = (index: number) => {
        setUploadingIndex(index);
        fileInputRef.current?.click();
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file && uploadingIndex !== null) {
            const localUrl = URL.createObjectURL(file);
            const updatedStats = [...stats];
            updatedStats[uploadingIndex] = {
                ...updatedStats[uploadingIndex],
                image: localUrl
            };
            onContentChange?.({ ...content, stats: updatedStats });
        }
        setUploadingIndex(null);
    };

    const handleRemoveImage = (index: number, e: React.MouseEvent) => {
        e.stopPropagation();
        const updatedStats = [...stats];
        updatedStats[index] = {
            ...updatedStats[index],
            image: ""
        };
        onContentChange?.({ ...content, stats: updatedStats });
    };

    return (
        <div
            className={`w-full py-10 md:py-12 ${isSelected ? "ring-2 ring-blue-500" : ""}`}
            style={{ backgroundColor: primaryBg, fontFamily }}
        >
            {/* Hidden File Input for Image Uploads */}
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
            />

            <div className="container mx-auto px-4 max-w-7xl">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 divide-y sm:divide-y-0 lg:divide-x divide-emerald-700/50">
                    {stats.map((stat: StatItem, idx: number) => (
                        <div
                            key={idx}
                            className="group relative flex items-center justify-center gap-5 py-6 sm:py-4 px-4 sm:px-6 transition-transform duration-300 hover:scale-105"
                        >
                            {/* Icon / Custom Uploaded Image with Translucent Background Circle Halo */}
                            <div className="relative shrink-0 flex items-center justify-center">
                                <div className="absolute w-14 h-14 rounded-full bg-emerald-600/40 -left-1 top-0 -z-0" />
                                
                                <div
                                    onClick={() => handleImageUpload(idx)}
                                    className="relative z-10 w-12 h-12 flex items-center justify-center cursor-pointer group/icon"
                                    title="Click to change icon/image"
                                >
                                    {stat.image ? (
                                        <img
                                            src={stat.image}
                                            alt={stat.label}
                                            className="w-10 h-10 object-contain drop-shadow-md rounded-lg"
                                        />
                                    ) : (
                                        iconMap[stat.icon || "course"] || iconMap.course
                                    )}

                                    {/* Hover Upload Trigger Icon */}
                                    <div className="absolute inset-0 bg-black/60 rounded-full opacity-0 group-hover/icon:opacity-100 transition-opacity flex items-center justify-center text-white">
                                        <PiUpload className="w-4 h-4" />
                                    </div>
                                </div>

                                {stat.image && (
                                    <button
                                        onClick={(e) => handleRemoveImage(idx, e)}
                                        className="absolute -top-1 -right-1 z-20 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                                        title="Remove custom image"
                                    >
                                        <PiX className="w-3 h-3" />
                                    </button>
                                )}
                            </div>

                            {/* Number and Label Stack */}
                            <div className="text-left">
                                <div className="text-3xl lg:text-4xl font-extrabold text-white tracking-tight leading-none mb-1.5">
                                    <EditableText
                                        value={stat.value}
                                        onChange={(v) => {
                                            const newStats = [...stats];
                                            newStats[idx] = { ...newStats[idx], value: v };
                                            onContentChange?.({ ...content, stats: newStats });
                                        }}
                                    />
                                </div>
                                <div className="text-xs sm:text-sm font-medium text-emerald-100/90 whitespace-nowrap">
                                    <EditableText
                                        value={stat.label}
                                        onChange={(v) => {
                                            const newStats = [...stats];
                                            newStats[idx] = { ...newStats[idx], label: v };
                                            onContentChange?.({ ...content, stats: newStats });
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
